import React, { useState, useEffect } from "react";
import { Patient } from "./PatientCard";
import PatientService from "../../services/patientService";

interface PatientEditFormProps {
  patient: Patient;
  onSave: (updatedPatient: Patient) => void;
  onCancel: () => void;
}

interface EditFormData {
  first_name: string;
  last_name: string;
  father_name: string;
  mother_name: string;
  birth_date: string;
  gender: "male" | "female";
  blood_type: string;
  phone: string;
  email: string;
  address: string;
  emergency_contact: {
    name: string;
    phone: string;
    relationship: string;
  };
  medical_notes: string;
  allergies: string[];
  chronic_conditions: string[];
  insurance_provider: string;
  insurance_number: string;
}

const PatientEditForm: React.FC<PatientEditFormProps> = ({
  patient,
  onSave,
  onCancel,
}) => {
  const [editData, setEditData] = useState<EditFormData>({
    first_name: patient.first_name,
    last_name: patient.last_name,
    father_name: patient.father_name || "",
    mother_name: patient.mother_name || "",
    birth_date: patient.birth_date,
    gender: patient.gender,
    blood_type: patient.blood_type || "",
    phone: patient.phone,
    email: patient.email || "",
    address: patient.address,
    emergency_contact: {
      name: patient.emergency_contact?.name || "",
      phone: patient.emergency_contact?.phone || "",
      relationship: patient.emergency_contact?.relationship || "",
    },
    medical_notes: patient.medical_notes || "",
    allergies: patient.allergies || [],
    chronic_conditions: patient.chronic_conditions || [],
    insurance_provider: patient.insurance_provider || "",
    insurance_number: patient.insurance_number || "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);

  const bloodTypes = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];
  const relationships = [
    "spouse",
    "parent",
    "child",
    "sibling",
    "friend",
    "other",
  ];

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;

    if (name.startsWith("emergency_")) {
      const field = name.replace("emergency_", "");
      setEditData((prev) => ({
        ...prev,
        emergency_contact: {
          ...prev.emergency_contact,
          [field]: value,
        },
      }));
    } else {
      setEditData((prev) => ({ ...prev, [name]: value }));
    }

    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleArrayInputChange = (
    field: "allergies" | "chronic_conditions",
    value: string
  ) => {
    const arrayValue = value
      ? value
          .split(",")
          .map((item) => item.trim())
          .filter((item) => item.length > 0)
      : [];
    setEditData((prev) => ({ ...prev, [field]: arrayValue }));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!editData.first_name.trim())
      newErrors.first_name = "First name is required";
    if (!editData.last_name.trim())
      newErrors.last_name = "Last name is required";
    if (!editData.birth_date) newErrors.birth_date = "Birth date is required";
    if (!editData.phone.trim()) newErrors.phone = "Phone number is required";
    if (!editData.address.trim()) newErrors.address = "Address is required";

    // Email validation if provided
    if (editData.email && !/\S+@\S+\.\S+/.test(editData.email)) {
      newErrors.email = "Invalid email format";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      const updatePayload = {
        firstName: editData.first_name,
        lastName: editData.last_name,
        fatherName: editData.father_name,
        motherName: editData.mother_name,
        birthDate: editData.birth_date,
        gender: editData.gender,
        bloodType: editData.blood_type,
        phone: editData.phone,
        email: editData.email,
        address: editData.address,
        emergencyContactName:
          editData.emergency_contact.name.trim() === ""
            ? "Not provided"
            : editData.emergency_contact.name,
        emergencyContactPhone:
          editData.emergency_contact.phone.trim() === ""
            ? "Not provided"
            : editData.emergency_contact.phone,
        emergencyContactRelationship:
          editData.emergency_contact.relationship === ""
            ? "Not provided"
            : editData.emergency_contact.relationship,
        medicalNotes: editData.medical_notes,
        allergies: editData.allergies.join(", "),
        chronicConditions: editData.chronic_conditions.join(", "),
        insuranceProvider: editData.insurance_provider,
        insuranceNumber: editData.insurance_number,
      };

      const updatedPatient = await PatientService.updatePatient(
        patient.id,
        updatePayload
      );
      onSave(updatedPatient);
    } catch (error: any) {
      setErrors({ submit: error.message || "Failed to update patient" });
    } finally {
      setIsLoading(false);
    }
  };

  const containerStyle: React.CSSProperties = {
    padding: "24px",
    height: "100%",
    overflow: "auto",
  };

  const formStyle: React.CSSProperties = {
    maxWidth: "800px",
    margin: "0 auto",
  };

  const sectionStyle: React.CSSProperties = {
    background: "#ffffff",
    borderRadius: "12px",
    padding: "24px",
    marginBottom: "24px",
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
    border: "1px solid #e5e7eb",
  };

  const sectionTitleStyle: React.CSSProperties = {
    fontSize: "18px",
    fontWeight: "600",
    color: "#1f2937",
    marginBottom: "20px",
    paddingBottom: "8px",
    borderBottom: "2px solid #f3f4f6",
  };

  const gridStyle: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
    gap: "20px",
    marginBottom: "20px",
  };

  const fieldStyle: React.CSSProperties = {
    marginBottom: "20px",
  };

  const labelStyle: React.CSSProperties = {
    display: "block",
    fontSize: "14px",
    fontWeight: "500",
    color: "#374151",
    marginBottom: "6px",
  };

  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: "10px 14px",
    border: "2px solid #e5e7eb",
    borderRadius: "8px",
    fontSize: "14px",
    outline: "none",
    transition: "border-color 0.2s ease",
  };

  const errorStyle: React.CSSProperties = {
    color: "#ef4444",
    fontSize: "12px",
    marginTop: "4px",
  };

  const buttonStyle = (
    variant: "primary" | "secondary" = "primary"
  ): React.CSSProperties => ({
    padding: "12px 24px",
    borderRadius: "8px",
    border: "none",
    fontSize: "14px",
    fontWeight: "500",
    cursor: "pointer",
    transition: "all 0.2s ease",
    background: variant === "primary" ? "#3b82f6" : "#e5e7eb",
    color: variant === "primary" ? "white" : "#374151",
  });

  return (
    <div style={containerStyle}>
      <div style={formStyle}>
        {/* Personal Information */}
        <div style={sectionStyle}>
          <h3 style={sectionTitleStyle}>👤 Personal Information</h3>

          <div style={gridStyle}>
            <div style={fieldStyle}>
              <label style={labelStyle} htmlFor="first_name">
                First Name *
              </label>
              <input
                type="text"
                id="first_name"
                name="first_name"
                value={editData.first_name}
                onChange={handleInputChange}
                style={inputStyle}
              />
              {errors.first_name && (
                <div style={errorStyle}>{errors.first_name}</div>
              )}
            </div>

            <div style={fieldStyle}>
              <label style={labelStyle} htmlFor="last_name">
                Last Name *
              </label>
              <input
                type="text"
                id="last_name"
                name="last_name"
                value={editData.last_name}
                onChange={handleInputChange}
                style={inputStyle}
              />
              {errors.last_name && (
                <div style={errorStyle}>{errors.last_name}</div>
              )}
            </div>
          </div>

          <div style={gridStyle}>
            <div style={fieldStyle}>
              <label style={labelStyle} htmlFor="father_name">
                Father's Name
              </label>
              <input
                type="text"
                id="father_name"
                name="father_name"
                value={editData.father_name}
                onChange={handleInputChange}
                style={inputStyle}
                placeholder="Father's full name"
              />
            </div>

            <div style={fieldStyle}>
              <label style={labelStyle} htmlFor="mother_name">
                Mother's Name
              </label>
              <input
                type="text"
                id="mother_name"
                name="mother_name"
                value={editData.mother_name}
                onChange={handleInputChange}
                style={inputStyle}
                placeholder="Mother's full name"
              />
            </div>
          </div>

          <div style={gridStyle}>
            <div style={fieldStyle}>
              <label style={labelStyle} htmlFor="birth_date">
                Birth Date *
              </label>
              <input
                type="date"
                id="birth_date"
                name="birth_date"
                value={editData.birth_date}
                onChange={handleInputChange}
                style={inputStyle}
              />
              {errors.birth_date && (
                <div style={errorStyle}>{errors.birth_date}</div>
              )}
            </div>

            <div style={fieldStyle}>
              <label style={labelStyle} htmlFor="gender">
                Gender
              </label>
              <select
                id="gender"
                name="gender"
                value={editData.gender}
                onChange={handleInputChange}
                style={inputStyle}
              >
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
            </div>

            <div style={fieldStyle}>
              <label style={labelStyle} htmlFor="blood_type">
                Blood Type
              </label>
              <select
                id="blood_type"
                name="blood_type"
                value={editData.blood_type}
                onChange={handleInputChange}
                style={inputStyle}
              >
                <option value="">Select blood type</option>
                {bloodTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div style={sectionStyle}>
          <h3 style={sectionTitleStyle}>📞 Contact Information</h3>

          <div style={gridStyle}>
            <div style={fieldStyle}>
              <label style={labelStyle} htmlFor="phone">
                Phone *
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={editData.phone}
                onChange={handleInputChange}
                style={inputStyle}
              />
              {errors.phone && <div style={errorStyle}>{errors.phone}</div>}
            </div>

            <div style={fieldStyle}>
              <label style={labelStyle} htmlFor="email">
                Email
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={editData.email}
                onChange={handleInputChange}
                style={inputStyle}
              />
              {errors.email && <div style={errorStyle}>{errors.email}</div>}
            </div>
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle} htmlFor="address">
              Address *
            </label>
            <textarea
              id="address"
              name="address"
              value={editData.address}
              onChange={handleInputChange}
              rows={3}
              style={{ ...inputStyle, resize: "none" }}
            />
            {errors.address && <div style={errorStyle}>{errors.address}</div>}
          </div>
        </div>

        {/* Emergency Contact */}
        <div style={sectionStyle}>
          <h3 style={sectionTitleStyle}>🚨 Emergency Contact</h3>

          <div style={gridStyle}>
            <div style={fieldStyle}>
              <label style={labelStyle} htmlFor="emergency_name">
                Name
              </label>
              <input
                type="text"
                id="emergency_name"
                name="emergency_name"
                value={editData.emergency_contact.name}
                onChange={handleInputChange}
                style={inputStyle}
              />
              {errors.emergency_name && (
                <div style={errorStyle}>{errors.emergency_name}</div>
              )}
            </div>

            <div style={fieldStyle}>
              <label style={labelStyle} htmlFor="emergency_phone">
                Phone
              </label>
              <input
                type="tel"
                id="emergency_phone"
                name="emergency_phone"
                value={editData.emergency_contact.phone}
                onChange={handleInputChange}
                style={inputStyle}
              />
              {errors.emergency_phone && (
                <div style={errorStyle}>{errors.emergency_phone}</div>
              )}
            </div>

            <div style={fieldStyle}>
              <label style={labelStyle} htmlFor="emergency_relationship">
                Relationship
              </label>
              <select
                id="emergency_relationship"
                name="emergency_relationship"
                value={editData.emergency_contact.relationship}
                onChange={handleInputChange}
                style={inputStyle}
              >
                <option value="">Select relationship</option>
                {relationships.map((rel) => (
                  <option key={rel} value={rel}>
                    {rel.charAt(0).toUpperCase() + rel.slice(1)}
                  </option>
                ))}
              </select>
              {errors.emergency_relationship && (
                <div style={errorStyle}>{errors.emergency_relationship}</div>
              )}
            </div>
          </div>
        </div>

        {/* Medical Information */}
        <div style={sectionStyle}>
          <h3 style={sectionTitleStyle}>🏥 Medical Information</h3>

          <div style={fieldStyle}>
            <label style={labelStyle} htmlFor="allergies">
              Allergies
            </label>
            <input
              type="text"
              id="allergies"
              name="allergies"
              value={editData.allergies.join(", ")}
              onChange={(e) =>
                handleArrayInputChange("allergies", e.target.value)
              }
              style={inputStyle}
              placeholder="Separate with commas"
            />
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle} htmlFor="chronic_conditions">
              Chronic Conditions
            </label>
            <input
              type="text"
              id="chronic_conditions"
              name="chronic_conditions"
              value={editData.chronic_conditions.join(", ")}
              onChange={(e) =>
                handleArrayInputChange("chronic_conditions", e.target.value)
              }
              style={inputStyle}
              placeholder="Separate with commas"
            />
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle} htmlFor="medical_notes">
              Medical Notes
            </label>
            <textarea
              id="medical_notes"
              name="medical_notes"
              value={editData.medical_notes}
              onChange={handleInputChange}
              rows={4}
              style={{ ...inputStyle, resize: "none" }}
              placeholder="Any additional medical information..."
            />
          </div>
        </div>

        {/* Insurance Information */}
        <div style={sectionStyle}>
          <h3 style={sectionTitleStyle}>🛡️ Insurance Information</h3>

          <div style={gridStyle}>
            <div style={fieldStyle}>
              <label style={labelStyle} htmlFor="insurance_provider">
                Insurance Provider
              </label>
              <select
                id="insurance_provider"
                name="insurance_provider"
                value={editData.insurance_provider}
                onChange={handleInputChange}
                style={inputStyle}
              >
                <option value="">Select insurance provider</option>
                <optgroup label="Public / Governmental">
                  <option value="National Social Security Fund (NSSF)">
                    National Social Security Fund (NSSF)
                  </option>
                  <option value="Civil Servants Cooperative">
                    Civil Servants Cooperative
                  </option>
                  <option value="Ministry of Public Health (MoPH)">
                    Ministry of Public Health (MoPH)
                  </option>
                  <option value="Lebanese Army Medical Services">
                    Lebanese Army Medical Services
                  </option>
                  <option value="Internal Security Forces Medical Services">
                    Internal Security Forces Medical Services
                  </option>
                  <option value="General Security Medical Services">
                    General Security Medical Services
                  </option>
                  <option value="State Security Medical Services">
                    State Security Medical Services
                  </option>
                  <option value="Judges' Mutual Fund">
                    Judges' Mutual Fund
                  </option>
                  <option value="Lebanese University Professors' Mutual Fund">
                    Lebanese University Professors' Mutual Fund
                  </option>
                </optgroup>
                <optgroup label="Private">
                  <option value="private_other">Private (Specify below)</option>
                </optgroup>
              </select>
            </div>

            {/* Show custom input field when "Private" is selected */}
            {editData.insurance_provider === "private_other" && (
              <div style={fieldStyle}>
                <label style={labelStyle} htmlFor="insurance_provider_custom">
                  Private Insurance Name
                </label>
                <input
                  type="text"
                  id="insurance_provider_custom"
                  name="insurance_provider_custom"
                  placeholder="Enter private insurance provider name"
                  style={inputStyle}
                />
              </div>
            )}

            <div style={fieldStyle}>
              <label style={labelStyle} htmlFor="insurance_number">
                Insurance Number
              </label>
              <input
                type="text"
                id="insurance_number"
                name="insurance_number"
                value={editData.insurance_number}
                onChange={handleInputChange}
                style={inputStyle}
              />
            </div>
          </div>
        </div>

        {/* Error Display */}
        {errors.submit && (
          <div
            style={{
              ...sectionStyle,
              background: "#fef2f2",
              border: "1px solid #fecaca",
              color: "#dc2626",
            }}
          >
            <strong>Error:</strong> {errors.submit}
          </div>
        )}

        {/* Action Buttons */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            padding: "24px 0",
          }}
        >
          <button
            id="cancel_button"
            name="cancel_button"
            onClick={onCancel}
            style={buttonStyle("secondary")}
            disabled={isLoading}
            onMouseEnter={(e) => (e.currentTarget.style.background = "#d1d5db")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "#e5e7eb")}
          >
            Cancel
          </button>

          <button
            id="save_button"
            name="save_button"
            onClick={handleSave}
            style={{
              ...buttonStyle("primary"),
              opacity: isLoading ? 0.7 : 1,
              cursor: isLoading ? "not-allowed" : "pointer",
            }}
            disabled={isLoading}
            onMouseEnter={(e) => {
              if (!isLoading) e.currentTarget.style.background = "#2563eb";
            }}
            onMouseLeave={(e) => {
              if (!isLoading) e.currentTarget.style.background = "#3b82f6";
            }}
          >
            {isLoading ? "💾 Saving..." : "💾 Save Changes"}
          </button>
        </div>
      </div>

      <style>
        {`
         input:focus, select:focus, textarea:focus {
           border-color: #3b82f6 !important;
           box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1) !important;
         }
       `}
      </style>
    </div>
  );
};

export default PatientEditForm;
